import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/admin";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const meta = document.querySelector('meta[name="robots"]');
        const prev = meta ? meta.getAttribute("content") : null;
        if (meta) {
            meta.setAttribute("content", "noindex, nofollow, noarchive");
        } else {
            const m = document.createElement("meta");
            m.name = "robots";
            m.content = "noindex, nofollow, noarchive";
            m.setAttribute("data-admin-noindex", "true");
            document.head.appendChild(m);
        }
        return () => {
            const cur = document.querySelector('meta[name="robots"]');
            if (cur && cur.getAttribute("data-admin-noindex") === "true") {
                cur.remove();
            } else if (cur && prev !== null) {
                cur.setAttribute("content", prev);
            }
        };
    }, []);

    useEffect(() => {
        if (user && user.role === "admin") {
            navigate(from, { replace: true });
        }
    }, [user, from, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError("");
        setSubmitting(true);
        const res = await login(email.trim().toLowerCase(), password);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Bienvenue.");
            navigate(from, { replace: true });
        } else {
            setError(res.error || "Identifiants invalides");
        }
    };

    return (
        <main
            data-testid="admin-login-page"
            className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F] flex items-center justify-center px-6"
        >
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md"
            >
                <div className="flex items-center gap-3 mb-8">
                    <span className="h-px w-10 bg-[#DC2626]" />
                    <span className="overline">Espace privé</span>
                </div>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                    Connexion<br />
                    <span className="italic">administrateur.</span>
                </h1>
                <p className="mt-4 text-[#64748B]">Accès réservé à l'équipe RO Concept.</p>

                <form onSubmit={onSubmit} data-testid="admin-login-form" className="mt-10 space-y-2">
                    <div>
                        <label className="overline text-[#64748B]">Email</label>
                        <input
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="admin-login-email"
                            className="field"
                            autoComplete="username"
                            placeholder="admin@meuble-etterbeek.be"
                        />
                    </div>
                    <div>
                        <label className="overline text-[#64748B]">Mot de passe</label>
                        <input
                            name="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="admin-login-password"
                            className="field"
                            autoComplete="current-password"
                            placeholder="••••••••"
                        />
                    </div>

                    {error && (
                        <div
                            data-testid="admin-login-error"
                            className="text-sm text-[#DC2626] pt-3"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="btn-primary mt-8 w-full justify-center disabled:opacity-60"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Connexion…
                            </>
                        ) : (
                            <>
                                <Lock size={16} strokeWidth={1.5} />
                                Se connecter
                            </>
                        )}
                    </button>
                </form>
            </motion.div>
        </main>
    );
}
